import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Badge, Button, Icon, Notification, Spinner, Text, useDateTimeFormat, useLocalizeMessage } from 'libs.nucleus';
import { AccessIcfPrompt } from '../components/access_icf_prompt/access_icf_prompt.component';
import { DocumentModal } from '../document_modal';
import { DocumentDetailFieldColumn } from './document_detail_fields.component';
import { DocumentSignersTable } from './document_signers_table.component';
import { useAccessIcfPrompt } from '../hooks/use_access_icf_prompt.hook';
import { usePtsDocumentAction } from '../participant_documents/hooks/use_pts_document_action.hook';
import { useVoidDocumentAction } from '../participant_documents/hooks/use_void_document_action.hook';
import { statusLabelMap } from '../utils/signed_document_status.helpers';
import { useCompletionDateAction } from './hooks/use_completion_date_action.hook';
import { useDocumentPdfCopy } from './hooks/use_document_pdf_copy.hook';
import { useEnableSigningAction } from './hooks/use_enable_signing_action.hook';
import { useReplacementUploadAction } from './hooks/use_replacement_upload_action.hook';
import { useSignNowAction } from './hooks/use_sign_now_action.hook';
import { useSignerInviteAction } from './hooks/use_signer_invite_action.hook';
import { useSiteDocument } from './hooks/use_site_document.hook';
import { buildSignerRows, getPrimaryParticipantNumber, getPtsCompletionDate, getVoidContext, hasDocumentPdfCopySource, isPtsDocument, isTemplateArchivedVoid, isWallclockDate, shouldDisplayFinalDocument, shouldShowEnableSigningBadge, shouldShowSignerRowActions, } from './site_document_details.helpers';
export const SiteDocumentDetailsView = ({ accountId, documentId, apiClient, getApiErrorMessage, accountEmail, accountDisplayName, isNoPiiSite, canOpenDocument, canDownloadConsentPdf, canModifyDocument, requiresIcfAccessWarning = false, orgCode, onLogout, classicDocumentUrl, siteLabel, }) => {
    const translate = useLocalizeMessage();
    const { formatDateTime, formatPlainDate } = useDateTimeFormat();
    const { document: signedDocument, invites, isLoading, areInvitesUnavailable, } = useSiteDocument({
        accountId,
        documentId,
        apiClient,
        getApiErrorMessage,
    });
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
    const [isArchiveBannerDismissed, setIsArchiveBannerDismissed] = useState(false);
    const { requestOpen, isPromptOpen, onConfirm, onCancel, onClose } = useAccessIcfPrompt(requiresIcfAccessWarning);
    const { openSignerInvite, signerInviteModal } = useSignerInviteAction({
        apiClient,
        documentId,
        documentTemplateId: signedDocument?.ec__document_template?._id,
        getApiErrorMessage,
    });
    const { canSignNowFor, signNow } = useSignNowAction({
        apiClient,
        orgCode,
        currentUserEmail: accountEmail,
        onLogout,
        getApiErrorMessage,
    });
    const { canVoidDocument, openVoidDocument, voidDocumentModal } = useVoidDocumentAction({
        canVoidConsents: canModifyDocument,
        isDocGenEconsentEnabled: true,
        apiClient,
        getApiErrorMessage,
    });
    const { canEnableSigning, enableSigning, isEnabling } = useEnableSigningAction({
        apiClient,
        getApiErrorMessage,
        canModifyDocument,
    });
    const { canEditCompletionDate, openCompletionDate, completionDateModal } = useCompletionDateAction({
        apiClient,
        accountId,
        getApiErrorMessage,
        canModifyDocument,
    });
    const { canReplaceSignedCopy, openReplacementUpload, replacementUploadModal } = useReplacementUploadAction({
        apiClient,
        accountId,
        getApiErrorMessage,
        canModifyDocument,
        isNoPiiSite,
    });
    const { openPtsDocument, ptsDocumentModal } = usePtsDocumentAction({
        apiClient,
        accountId,
        isNoPiiSite,
        isDocGenEconsentEnabled: true,
        getApiErrorMessage,
        canDownloadConsentPdf,
        canVoidDocument,
        onVoidDocument: openVoidDocument,
    });
    const isPts = isPtsDocument(signedDocument);
    // Print needs the Studio bundle (a fetchable blob). A legacy File URL can still
    // be Downloaded by navigating to it; offering Print there would only toast.
    const showDownloadAction = canDownloadConsentPdf && hasDocumentPdfCopySource(signedDocument, 'download');
    const showPrintAction = canDownloadConsentPdf && hasDocumentPdfCopySource(signedDocument, 'print');
    const { copyConsentPdf, isCopyingPdf } = useDocumentPdfCopy({
        document: signedDocument,
        enabled: showDownloadAction || showPrintAction,
        isNoPiiSite,
    });
    const openDocument = useCallback((document) => {
        if (isPtsDocument(document)) {
            requestOpen(() => openPtsDocument(document));
            return;
        }
        requestOpen(() => setIsDocumentModalOpen(true));
    }, [openPtsDocument, requestOpen]);
    if (isLoading) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    // A document whose template is missing is treated as not found rather than rendered
    // with an empty title and heading. Classic threw a 404 redirect here (EC-795), so this
    // does happen in production.
    if (!signedDocument || !signedDocument.ec__document_template) {
        return (_jsx(Text, { size: 'sm', className: 'text-text-secondary', children: translate('error.site_document_details.not_found') }));
    }
    if (!canOpenDocument) {
        return (_jsx(Text, { size: 'sm', className: 'text-text-secondary', dataTestId: 'document-detail-unavailable', children: translate('error.participant_documents.unavailable_for_role') }));
    }
    const isArchivedVoid = isTemplateArchivedVoid(signedDocument);
    const voidContext = getVoidContext(signedDocument);
    const voidedByDescription = voidContext?.ec__source === 'system'
        ? translate('label.site_document_details.voided_by.system')
        : voidContext?.ec__actor_name?.trim() || '';
    const cancellationReason = signedDocument.ec__cancellation_reason?.trim() ?? '';
    const participantNumber = getPrimaryParticipantNumber(signedDocument);
    const participantLabel = participantNumber
        ? translate('label.site_document_details.participant', { number: participantNumber })
        : '';
    const headerSubtitle = [participantLabel, siteLabel].filter(Boolean).join('  ·  ');
    // A failed invites read means the invite state is unknown, not empty. Projecting the
    // template roles against an empty invite list blanks out every email and status while
    // the completion dates still resolve — those come off the document query, which
    // succeeded — so a fully executed consent would render as a table of anonymous signers
    // with real signing dates. The table reports that it could not load instead.
    const signerRows = areInvitesUnavailable ? [] : buildSignerRows(signedDocument, invites);
    /**
     * Classic laid these out in two columns and the grouping is meaningful: identity and
     * completion on the left, lifecycle on the right. Preserved rather than collapsed into
     * one list so the page reads the same as the view site users know.
     */
    const identityItems = [
        {
            title: translate('label.site_document_details.id'),
            description: signedDocument._id,
            dataTestId: 'document-detail-id',
        },
        {
            title: translate('label.site_document_details.document_template'),
            description: signedDocument.ec__document_template?.ec__title ?? '',
            dataTestId: 'document-detail-template',
        },
        {
            title: translate('label.site_document_details.type'),
            // Derived from the same predicate as the action set so the two cannot disagree —
            // a Printed document offering digital invites was the defect this replaced.
            description: isPts
                ? translate('label.site_document_details.printed')
                : translate('label.site_document_details.digital'),
            dataTestId: 'document-detail-type',
        },
        {
            // A wet signature is captured as a date with no meaningful time of day, and it is
            // stored timezone-free, so it goes through the plain-date formatter rather than
            // the instant one — see getPtsCompletionDate.
            title: isPts
                ? translate('label.site_document_details.completion_date')
                : translate('label.site_document_details.completion_date_time'),
            description: isPts ? (_jsxs("span", { className: 'flex items-center gap-2', children: [formatPlainDate(getPtsCompletionDate(signedDocument)), canEditCompletionDate(signedDocument) && (_jsx(Button, { variant: 'transparent', size: 'sm', 
                        // `content` renders icon-only; `label` still supplies the accessible name.
                        label: translate('action.site_document_details.edit_completion_date'), content: _jsx(Icon, { name: 'edit', size: 'sm' }), onClick: () => openCompletionDate(signedDocument), dataTestId: 'edit-completion-date-button' }))] })) : signedDocument.ec__completion_time ? (
            // A digital document stores a real instant, but the value decides: a bare date
            // here would shift a day through the instant formatter, so it takes the same
            // plain-date path regardless of which PTS flag put it there.
            isWallclockDate(signedDocument.ec__completion_time) ? (formatPlainDate(signedDocument.ec__completion_time)) : (formatDateTime(signedDocument.ec__completion_time))) : (''),
            dataTestId: 'document-detail-completion-time',
        },
    ];
    const lifecycleItems = [
        {
            title: translate('label.site_document_details.status'),
            description: translate(statusLabelMap[signedDocument.ec__status]),
            tone: signedDocument.ec__status === 'cancelled' || signedDocument.ec__status === 'voided' ? 'danger' : 'status',
            dataTestId: 'document-detail-status',
        },
        {
            title: translate('label.site_document_details.created'),
            description: signedDocument.created ? formatDateTime(signedDocument.created) : '',
            dataTestId: 'document-detail-created',
        },
        {
            title: translate('label.site_document_details.reason_for_void'),
            description: cancellationReason,
            tone: isArchivedVoid ? 'warning' : 'default',
            hidden: !cancellationReason,
            dataTestId: 'document-detail-void-reason',
        },
        {
            title: translate('label.site_document_details.voided_by'),
            description: voidedByDescription,
            hidden: !isArchivedVoid,
            dataTestId: 'document-detail-voided-by',
        },
        {
            title: translate('label.site_document_details.signed_uploaded_document'),
            // This is the signed wet-ink consent itself, so it is behind the same download
            // permission the PTS modal enforces, and behind the ICF acknowledgement the rest of
            // the page routes document access through. `noopener` is correct here, unlike in
            // the sign-now hand-off, because no window handle is kept.
            description: signedDocument.ec__final_document?.url && canDownloadConsentPdf ? (_jsx(Button, { variant: 'transparent', 
                // The scan's own name identifies which document was uploaded, which a bare
                // "Download" cannot. It is the browser file's `name` at upload time, so it is
                // a real user-facing name — and frequently a participant-identifying one.
                //
                // A role that must be warned before reaching consent content therefore sees
                // only the generic label: this label renders on load, ahead of any
                // acknowledgement, and a modified click on the title never raises the prompt
                // at all. The host withholds the download permission from those same roles
                // today, but the two props arrive here independently and only this one
                // governs the name. Cortex strips null keys off the file facet, so a scan
                // stored without a name falls back the same way.
                label: (!requiresIcfAccessWarning && signedDocument.ec__final_document.filename) ||
                    translate('action.site_document_details.download'), onClick: () => requestOpen(() => window.open(signedDocument.ec__final_document?.url, '_blank', 'noopener,noreferrer')), dataTestId: 'document-detail-final-document' })) : (''),
            // Classic only surfaced this row for a wet-signature document that had one uploaded.
            hidden: !shouldDisplayFinalDocument(signedDocument),
            dataTestId: 'document-detail-final-document-row',
        },
    ];
    return (_jsxs("div", { className: 'flex flex-col', style: { gap: 18 }, children: [_jsxs("div", { className: 'flex items-start justify-between', style: { gap: 14 }, children: [_jsxs("div", { className: 'flex flex-col', style: { gap: 3 }, children: [_jsxs("div", { className: 'flex items-center gap-2', children: [_jsx("h1", { className: 'm-0 text-text-primary', "data-testid": 'document-detail-title', style: { fontSize: 22, fontWeight: 600, lineHeight: '29px' }, children: signedDocument.ec__document_template?.ec__title ?? '' }), shouldShowEnableSigningBadge(signedDocument) && (_jsx(Badge, { variant: signedDocument.ec__can_sign_document ? 'success' : 'incomplete', label: signedDocument.ec__can_sign_document
                                            ? translate('label.site_document_details.signing_enabled')
                                            : translate('label.site_document_details.signing_read_only') }))] }), headerSubtitle ? (_jsx("p", { className: 'm-0 text-neutral-500', "data-testid": 'document-detail-subtitle', style: { fontSize: 13, lineHeight: '18px' }, children: headerSubtitle })) : null] }), _jsxs("div", { className: 'flex items-center', style: { gap: 10 }, children: [showDownloadAction && (_jsx(Button, { variant: 'secondary', label: translate('action.participant_documents.download'), onClick: () => {
                                    void copyConsentPdf('download');
                                }, disabled: isCopyingPdf, dataTestId: 'document-detail-download' })), showPrintAction && (_jsx(Button, { variant: 'secondary', label: translate('action.participant_documents.print'), onClick: () => {
                                    void copyConsentPdf('print');
                                }, disabled: isCopyingPdf, dataTestId: 'document-detail-print' })), canReplaceSignedCopy(signedDocument) && (_jsx(Button, { variant: 'secondary', label: translate('action.site_document_details.upload_new_document'), onClick: () => openReplacementUpload(signedDocument), dataTestId: 'upload-new-document-button' })), canVoidDocument(signedDocument) && (_jsx(Button, { variant: 'secondary', label: translate('action.site_document_details.void'), onClick: () => openVoidDocument(signedDocument), dataTestId: 'void-document-button' })), canOpenDocument && (_jsx(Button, { variant: 'secondary', label: isPts
                                    ? translate('action.site_document_details.manage_printed_document')
                                    : translate('action.site_document_details.view_document'), onClick: () => openDocument(signedDocument), dataTestId: 'view-document-button' })), canEnableSigning(signedDocument) && (_jsx(Button, { variant: 'primary', label: translate('action.site_document_details.enable_signing'), onClick: () => enableSigning(signedDocument), disabled: isEnabling, dataTestId: 'enable-signing-button' }))] })] }), isArchivedVoid && !isArchiveBannerDismissed ? (_jsx("div", { "data-testid": 'document-archive-banner', children: _jsx(Notification, { variant: 'inline', type: 'warning', customIcon: 'warning', title: translate('info.site_document_details.archive_void.title'), subtitle: _jsx("span", { className: 'nucleus-text-sm nucleus-text-text-warning', children: translate('info.site_document_details.archive_void.body') }), closeable: true, onClose: () => setIsArchiveBannerDismissed(true) }) })) : null, _jsxs("div", { className: 'grid grid-cols-1 md:grid-cols-2', style: { columnGap: 18 }, children: [_jsx(DocumentDetailFieldColumn, { items: identityItems, dataTestId: 'document-detail-identity' }), _jsx(DocumentDetailFieldColumn, { items: lifecycleItems, dataTestId: 'document-detail-lifecycle' })] }), _jsxs("div", { className: 'flex flex-col', style: { gap: 8 }, children: [_jsx("h2", { className: 'm-0 text-text-primary', style: { fontSize: 14, fontWeight: 600, lineHeight: '20px' }, children: translate('label.site_document_details.signers.title') }), _jsx(DocumentSignersTable, { rows: signerRows, showRowActions: canModifyDocument && !areInvitesUnavailable && shouldShowSignerRowActions(signedDocument), onOpenSignerInvite: openSignerInvite, canSignNowFor: canSignNowFor, onSignNow: signNow, emptyLabel: translate(areInvitesUnavailable
                            ? 'error.site_document_details.signers.unavailable'
                            : 'label.site_document_details.signers.empty') })] }), ptsDocumentModal, voidDocumentModal, completionDateModal, replacementUploadModal, signerInviteModal, _jsx(AccessIcfPrompt, { isPromptOpen: isPromptOpen, onConfirm: onConfirm, onCancel: onCancel, onClose: onClose }), _jsx(DocumentModal, { document: isDocumentModalOpen ? signedDocument : null, isOpen: isDocumentModalOpen, onClose: () => setIsDocumentModalOpen(false), accountId: accountId, accountEmail: accountEmail, accountDisplayName: accountDisplayName, apiClient: apiClient, getApiErrorMessage: getApiErrorMessage, isDocGenEconsentEnabled: true, classicDocumentUrl: classicDocumentUrl, canDownloadConsentPdf: canDownloadConsentPdf })] }));
};
